import { NodeOperation } from '../types/projectData';
import { ApplicationSnapshot, PartialApplicationSnapshot, SnapshotRestorationOptions } from '../types/applicationState';
import { SnapshotMetrics } from './snapshotPerformance';
import { CompressedSnapshot, snapshotCompressor } from './snapshotCompression';
import { deepClone } from './clone';
import { log } from './logger';
import { SnapshotPerformanceAnalyzer, PerformanceMetrics, PerformanceReport } from './snapshotPerformanceAnalyzer';
import { SnapshotProcessor } from './snapshotProcessor';
import { SnapshotRestorationService } from './snapshotRestorationService';
import { SnapshotConfigManager, SnapshotConfigOptions } from './snapshotConfigManager';

export interface HistorySnapshot {
  id: string;
  timestamp: number;
  operation: NodeOperation;
  state: ApplicationSnapshot;
  // Track what changed in this snapshot
  changes?: {
    hasDataChanges: boolean;
    hasViewStateChanges: boolean;
    hasUIStateChanges: boolean;
    affectedFeatures: string[];
  };
  // Performance metrics
  metrics?: SnapshotMetrics;
  // Compressed storage
  compressed?: CompressedSnapshot;
}

export class SnapshotHistory {
  private snapshots: HistorySnapshot[] = [];
  private currentIndex: number = -1;
  private maxSnapshots: number = 100;
  private enableCompression: boolean = true;
  private enablePerformanceMonitoring: boolean = true;
  private backgroundProcessing: boolean = false;
  private pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(options: {
    maxSnapshots?: number;
    enableCompression?: boolean;
    enablePerformanceMonitoring?: boolean;
    backgroundProcessing?: boolean;
  } = {}) {
    if (options.maxSnapshots !== undefined && Number.isInteger(options.maxSnapshots) && options.maxSnapshots > 0) {
      this.maxSnapshots = options.maxSnapshots;
    }
    this.enableCompression = options.enableCompression ?? true;
    this.enablePerformanceMonitoring = options.enablePerformanceMonitoring ?? true;
    this.backgroundProcessing = options.backgroundProcessing ?? false;
  }

  /**
   * Add a new snapshot to history
   * This will:
   * 1. Remove any redo states (snapshots after currentIndex)
   * 2. Add the new snapshot with performance monitoring
   * 3. Apply compression if enabled
   * 4. Remove oldest snapshots if we exceed maxSnapshots
   */
  addSnapshot(state: ApplicationSnapshot, operation: NodeOperation, changes?: HistorySnapshot['changes']): void {
    const clonedState = this.cloneApplicationState(state);
    // Remove any redo states (everything after currentIndex)
    if (this.currentIndex < this.snapshots.length - 1) {
      const removed = this.snapshots.splice(this.currentIndex + 1);
      this.releaseSnapshots(removed);
    }

    const snapshotId = `snapshot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // For critical operations that affect undo state, we need to update the index synchronously
    // to ensure canUndo() returns the correct value immediately
    const tempSnapshot: HistorySnapshot = {
      id: snapshotId,
      timestamp: Date.now(),
      operation,
      state: clonedState,
      changes,
      metrics: undefined,
      compressed: undefined
    };
    
    // Add to history immediately to ensure correct undo state
    this.snapshots.push(tempSnapshot);
    this.currentIndex++;
    
    // Bound history and pending work before yielding to any deferred processor.
    this.cleanupOldSnapshots();
    if (this.backgroundProcessing) {
      // Capture only the ID; eviction cancels timers and releases their state.
      this.pendingTimers.set(snapshotId, setTimeout(() => {
        this.pendingTimers.delete(snapshotId);
        this.processSnapshotDeferred(snapshotId).catch(error => {
          log.error('SnapshotHistory', 'Error processing snapshot:', error);
        });
      }, 0));
    } else {
      this.processSnapshotDeferred(snapshotId).catch(error => {
        log.error('SnapshotHistory', 'Error processing snapshot:', error);
      });
    }
  }

  private async processSnapshotDeferred(snapshotId: string): Promise<void> {
    const index = this.snapshots.findIndex(snapshot => snapshot.id === snapshotId);
    if (index < 0) return;
    const snapshot = this.snapshots[index];
    const previousSnapshot = this.snapshots[index - 1]?.state;
    try {
      const processed = await SnapshotProcessor.processSnapshotImmediate(
        snapshot.state,
        snapshot.operation,
        snapshot.changes,
        snapshotId,
        this.enablePerformanceMonitoring,
        this.enableCompression,
        previousSnapshot
      );
      // Indices change after eviction, branching and clear; only identity is stable.
      const currentIndex = this.snapshots.findIndex(entry => entry.id === snapshotId);
      if (currentIndex >= 0) {
        this.snapshots[currentIndex] = { ...processed, timestamp: snapshot.timestamp };
      } else {
        snapshotCompressor.release(snapshotId);
      }
    } catch (error) {
      snapshotCompressor.release(snapshotId);
      throw error;
    }
  }

  private releaseSnapshots(snapshots: HistorySnapshot[]): void {
    for (const snapshot of snapshots) {
      const timer = this.pendingTimers.get(snapshot.id);
      if (timer !== undefined) clearTimeout(timer);
      this.pendingTimers.delete(snapshot.id);
      snapshotCompressor.release(snapshot.id);
    }
  }

  private cleanupOldSnapshots(): void {
    if (this.snapshots.length > this.maxSnapshots) {
      const removeCount = this.snapshots.length - this.maxSnapshots;
      const removed = this.snapshots.splice(0, removeCount);
      this.currentIndex = Math.max(0, this.currentIndex - removeCount);
      this.releaseSnapshots(removed);
    }
  }

  /**
   * Get the current state
   */
  getCurrentState(): ApplicationSnapshot | null {
    if (this.currentIndex >= 0 && this.currentIndex < this.snapshots.length) {
      return this.cloneApplicationState(this.snapshots[this.currentIndex].state);
    }
    return null;
  }

  /**
   * Undo to previous state
   * Returns the previous state or null if can't undo
   */
  undo(): ApplicationSnapshot | null {
    if (this.canUndo()) {
      this.currentIndex--;
      return this.getCurrentState();
    }
    return null;
  }

  /**
   * Redo to next state
   * Returns the next state or null if can't redo
   */
  redo(): ApplicationSnapshot | null {
    if (this.canRedo()) {
      this.currentIndex++;
      return this.getCurrentState();
    }
    return null;
  }

  /**
   * Check if undo is possible
   */
  canUndo(): boolean {
    return this.currentIndex > 0;
  }

  /**
   * Check if redo is possible
   */
  canRedo(): boolean {
    return this.currentIndex < this.snapshots.length - 1;
  }

  /**
   * Get undo/redo counts for UI
   */
  getUndoRedoCounts(): { undoCount: number; redoCount: number } {
    return {
      undoCount: this.currentIndex,
      redoCount: this.snapshots.length - 1 - this.currentIndex
    };
  }

  /**
   * Get history list for UI (like Blender's undo history panel)
   */
  getHistoryList(): Array<{
    id: string;
    description: string;
    timestamp: number;
    isActive: boolean;
    canNavigate: boolean;
    changes?: HistorySnapshot['changes'];
  }> {
    return this.snapshots.map((snapshot, index) => ({
      id: snapshot.id,
      description: snapshot.operation.description || `Operation: ${snapshot.operation.type}`,
      timestamp: snapshot.timestamp,
      isActive: index === this.currentIndex,
      canNavigate: true,
      changes: snapshot.changes
    }));
  }

  /**
   * Jump to specific history position
   */
  jumpToPosition(index: number): ApplicationSnapshot | null {
    if (index >= 0 && index < this.snapshots.length) {
      this.currentIndex = index;
      return this.getCurrentState();
    }
    return null;
  }
  
  /**
   * Undo/redo with selective restoration options
   */
  undoSelective(options: SnapshotRestorationOptions): PartialApplicationSnapshot | null {
    if (this.canUndo()) {
      this.currentIndex--;
      const snapshot = this.snapshots[this.currentIndex];
      return SnapshotRestorationService.extractPartialSnapshot(snapshot.state, options);
    }
    return null;
  }

  redoSelective(options: SnapshotRestorationOptions): PartialApplicationSnapshot | null {
    if (this.canRedo()) {
      this.currentIndex++;
      const snapshot = this.snapshots[this.currentIndex];
      return SnapshotRestorationService.extractPartialSnapshot(snapshot.state, options);
    }
    return null;
  }

  /**
   * Clear all history
   */
  clear(): void {
    this.releaseSnapshots(this.snapshots);
    this.snapshots = [];
    this.currentIndex = -1;
  }

  /**
   * Get current position info
   */
  getPositionInfo(): {
    current: number;
    total: number;
    percentage: number;
  } {
    return {
      current: this.currentIndex + 1,
      total: this.snapshots.length,
      percentage: this.snapshots.length > 0 
        ? Math.round(((this.currentIndex + 1) / this.snapshots.length) * 100)
        : 0
    };
  }

  /**
   * Deep clone application state to ensure immutability
   */
  private cloneApplicationState(state: ApplicationSnapshot): ApplicationSnapshot {
    return deepClone(state);
  }
  
  /**
   * Get changes between two snapshots
   */
  getChangesBetween(fromIndex: number, toIndex: number): HistorySnapshot['changes'][] {
    const changes: HistorySnapshot['changes'][] = [];
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    
    for (let i = start + 1; i <= end && i < this.snapshots.length; i++) {
      if (this.snapshots[i].changes) {
        changes.push(this.snapshots[i].changes);
      }
    }
    
    return changes;
  }

  /**
   * Get performance metrics for snapshots
   */
  getPerformanceMetrics(): PerformanceMetrics {
    return SnapshotPerformanceAnalyzer.getPerformanceMetrics(this.snapshots, this.enableCompression);
  }

  /**
   * Get performance report with recommendations
   */
  getPerformanceReport(): PerformanceReport {
    return SnapshotPerformanceAnalyzer.getPerformanceReport(this.snapshots, this.enableCompression);
  }

  /**
   * Configure snapshot system settings
   */
  configure(options: SnapshotConfigOptions): void {
    if (options.maxSnapshots !== undefined && Number.isInteger(options.maxSnapshots) && options.maxSnapshots > 0) {
      this.maxSnapshots = options.maxSnapshots;
      this.cleanupOldSnapshots(); // Apply new limit immediately
    }

    if (options.enableCompression !== undefined) {
      this.enableCompression = options.enableCompression;
    }

    if (options.enablePerformanceMonitoring !== undefined) {
      this.enablePerformanceMonitoring = options.enablePerformanceMonitoring;
    }

    if (options.backgroundProcessing !== undefined) {
      this.backgroundProcessing = options.backgroundProcessing;
    }
  }

  /**
   * Get current configuration
   */
  getConfiguration() {
    const metrics = this.getPerformanceMetrics();
    return SnapshotConfigManager.getConfiguration(
      this.maxSnapshots,
      this.enableCompression,
      this.enablePerformanceMonitoring,
      this.backgroundProcessing,
      this.snapshots.length,
      metrics.averageSnapshotSize
    );
  }
}